"use client";

import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useToast } from "@/context/ToastContext";
import { t } from "@/lib/site-texts";

// Het "contactformulier"-paginablok: plaatst het bestaande contactformulier +
// gegevens + kaart in een CMS-pagina. Dit is functionaliteit (formulier met
// validatie/verzending), geen platte inhoud, dus het blijft een eigen
// component i.p.v. een generiek tekst-/HTML-blok. De tekst erin blijft, net
// als voorheen, bewerkbaar via Admin → Live-editor → Contact ons
// (data-t-key), NIET via de blokkeneditor.

const IconPin = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);

const IconPhone = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.07 12 19.79 19.79 0 0 1 1 3.18 2 2 0 0 1 2.96 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);

const IconMail = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <polyline points="2,4 12,13 22,4"/>
  </svg>
);

export default function ContactFormBlock() {
  const { showToast } = useToast();
  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  // MOW-NOV26 spam: een formulier dat te snel verzonden wordt is een robot (gemeten vanaf het tonen)
  const geopendOp = useRef<number | null>(null);
  useEffect(() => { geopendOp.current = Date.now(); }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSending(true);
    try {
      // >>> MOW-NOV26 spam: het verborgen veld wordt uit het formulier gelezen (een robot zet de waarde
      // soms zonder een toetsaanslag, dus niet via de React-state) en de invulduur gaat mee.
      const homepage = String(new FormData(e.currentTarget).get("homepage") ?? "");
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, homepage, invulduur: geopendOp.current === null ? undefined : Date.now() - geopendOp.current }),
      });
      if (res.status === 422) {
        const antwoord = await res.json().catch(() => null);
        showToast(antwoord?.error || "Dit bericht kon niet verzonden worden. Mail ons gerust op info@mijnondergoedwinkel.nl.", "error");
        return;
      }
      // <<< MOW-NOV26 spam
      if (!res.ok) {
        throw new Error("Verzenden mislukt");
      }
      showToast("Bericht succesvol verzonden. We nemen zo snel mogelijk contact op.", "success");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      showToast("Er ging iets mis bij het verzenden. Probeer het later opnieuw.", "error");
    } finally {
      setSending(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid var(--color-gray-200)",
    borderRadius: "3px",
    fontSize: "0.9rem",
    background: "#fff",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "var(--color-secondary)",
    marginBottom: "6px",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  };

  const iconCircle: React.CSSProperties = {
    width: "38px",
    height: "38px",
    borderRadius: "50%",
    background: "#f7eaea",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--color-primary)",
    flexShrink: 0,
  };

  return (
    <>
      {/* Hero title */}
      <section style={{ textAlign: "center", marginBottom: "50px" }}>
        <h1 data-t-key="contact.hero.title" style={{ fontSize: "2.5rem", fontWeight: 800, color: "var(--color-secondary)", fontFamily: "var(--font-serif)", marginBottom: "16px" }}>
          {t("contact.hero.title", "Contact ons")}
        </h1>
        <p data-t-key="contact.hero.intro" style={{ fontSize: "1rem", color: "var(--color-gray-500)", maxWidth: "600px", margin: "0 auto", lineHeight: 1.7 }}>
          {t("contact.hero.intro", "Heb je een vraag over een bestelling, product of maat? We helpen je graag verder. Neem gerust contact met ons op.")}
        </p>
      </section>

      {/* Form + info */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "48px", alignItems: "start" }}>

        {/* Contactformulier */}
        <div style={{ background: "#ffffff", padding: "40px", borderRadius: "8px", boxShadow: "var(--shadow-sm)" }}>
          <h2 data-t-key="contact.form.title" style={{ fontSize: "1.4rem", fontWeight: 700, color: "var(--color-secondary)", marginBottom: "24px", fontFamily: "var(--font-serif)" }}>
            {t("contact.form.title", "Stuur ons een bericht")}
          </h2>
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div>
              <label htmlFor="name" style={labelStyle}>Naam</label>
              <input type="text" id="name" required value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label htmlFor="email" style={labelStyle}>E-mailadres</label>
              <input type="email" id="email" required value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label htmlFor="subject" style={labelStyle}>Onderwerp</label>
              <input type="text" id="subject" required value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label htmlFor="message" style={labelStyle}>Bericht</label>
              <textarea id="message" required rows={5} value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            {/* MOW-NOV26 spam: verborgen val voor formulierrobots. Buiten beeld (niet display:none, dat slaan
                robots over), niet te bereiken met Tab en voor een schermlezer weggelaten. */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }}>
              <label htmlFor="homepage">Niet invullen</label>
              <input type="text" id="homepage" name="homepage" tabIndex={-1} autoComplete="off" />
            </div>
            <button type="submit" disabled={sending} className="btn btn-primary"
              style={{ width: "100%", padding: "14px 0", marginTop: "8px", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.05em" }}>
              {sending ? "Verzenden…" : <span data-t-key="contact.form.submit">{t("contact.form.submit", "Bericht verzenden")}</span>}
            </button>
          </form>
        </div>

        {/* Gegevens + kaart */}
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>

          {/* Contactgegevens */}
          <div style={{ background: "#ffffff", padding: "36px 40px", borderRadius: "8px", boxShadow: "var(--shadow-sm)" }}>
            <h2 data-t-key="contact.details.title" style={{ fontSize: "1.4rem", fontWeight: 700, color: "var(--color-secondary)", marginBottom: "28px", fontFamily: "var(--font-serif)" }}>
              {t("contact.details.title", "Onze gegevens")}
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>

              <div style={{ display: "flex", gap: "14px", alignItems: "center" }}>
                <div style={iconCircle}><IconPin /></div>
                <p style={{ fontSize: "0.875rem", color: "var(--color-gray-500)", margin: 0, lineHeight: 1.6 }}>
                  <span data-t-key="contact.details.address-line1">{t("contact.details.address-line1", "De Strookappe 20")}</span><br /><span data-t-key="contact.details.address-line2">{t("contact.details.address-line2", "7491 JB Delden")}</span>
                </p>
              </div>

              <div style={{ display: "flex", gap: "14px", alignItems: "center" }}>
                <div style={iconCircle}><IconPhone /></div>
                <a href="tel:+31623399503" data-t-key="contact.details.phone" style={{ fontSize: "0.875rem", color: "var(--color-gray-500)", textDecoration: "none" }}>{t("contact.details.phone", "+31 6 233 99 503")}</a>
              </div>

              <div style={{ display: "flex", gap: "14px", alignItems: "center" }}>
                <div style={iconCircle}><IconMail /></div>
                <a href="mailto:info@mijnondergoedwinkel.nl" data-t-key="contact.details.email" style={{ fontSize: "0.875rem", color: "var(--color-gray-500)", textDecoration: "none" }}>{t("contact.details.email", "info@mijnondergoedwinkel.nl")}</a>
              </div>

            </div>
          </div>

          {/* OpenStreetMap embed */}
          <div style={{ borderRadius: "8px", overflow: "hidden", boxShadow: "var(--shadow-sm)", lineHeight: 0 }}>
            <iframe
              title="Locatie Mijnondergoedwinkel.nl — De Strookappe 20, Delden"
              src="https://www.openstreetmap.org/export/embed.html?bbox=6.7090%2C52.2490%2C6.7370%2C52.2600&layer=mapnik&marker=52.25431%2C6.72295"
              width="100%"
              height="280"
              style={{ border: "none", display: "block" }}
              loading="lazy"
            />
          </div>
        </div>
      </div>

      {/* Kom je er niet helemaal uit? */}
      <div style={{ marginTop: "48px", background: "#fff", padding: "32px 36px", borderRadius: "8px", boxShadow: "var(--shadow-sm)", textAlign: "center" }}>
        <h2 data-t-key="contact.help.title" style={{ fontSize: "1.3rem", fontFamily: "var(--font-serif)", color: "var(--color-secondary)", marginBottom: "10px" }}>
          {t("contact.help.title", "Kom je er niet helemaal uit?")}
        </h2>
        <p style={{ fontSize: "0.9rem", color: "var(--color-gray-500)", maxWidth: "60ch", margin: "0 auto 18px" }}>
          <span data-t-key="contact.help.body-before">{t("contact.help.body-before", "Bekijk eerst onze veelgestelde vragen — grote kans dat je antwoord er al tussen staat. Lukt het niet? Mail ons gerust op")}</span> <a href="mailto:info@mijnondergoedwinkel.nl" style={{ color: "var(--color-primary)" }}>info@mijnondergoedwinkel.nl</a><span data-t-key="contact.help.body-after">{t("contact.help.body-after", ", we helpen je zo snel mogelijk verder.")}</span>
        </p>
        <Link href="/veelgestelde-vragen" className="btn btn-outline" data-t-key="contact.help.cta" style={{ padding: "10px 20px" }}>
          {t("contact.help.cta", "Naar de veelgestelde vragen")}
        </Link>
      </div>
    </>
  );
}
